import { IMPRESSION_ACTIONS, SCREEN_NAME, CLICK_ACTIONS } from "@payment-client/analytics/constants";
import { stringifyWithUndefinedKeys } from "@payment-client/analytics/helpers";
import { AnalyticsContextValue } from "@swiggy-private/checkout-core";
import { LABELS } from "@payment-client/analytics/constants";

export interface AdditionalDuplicateOrderClickParams {
    orderId?: string;
    subtitle?: string;
    title?: string;
    businessLine?: string;
    errorStatusCode?: number;
    actionName?: string;
}

export const duplicateOrderDialogImpressionEvent = (
    cartId: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    additionalDetails: AdditionalDuplicateOrderClickParams,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT,
        action: IMPRESSION_ACTIONS.IMPRESSION_DUPLICATE_ORDER_DIALOG,
        context: stringifyWithUndefinedKeys({
            cartId,
            ...additionalDetails,
        }),
    });
};

export const duplicationOrderDialogClickActionEvent = (
    actionName: string,
    cartId: string,
    clickEvent: AnalyticsContextValue["clickEvent"],
    additionalDetails: AdditionalDuplicateOrderClickParams,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT,
        action: CLICK_ACTIONS.CLICK_DUPLICATE_ORDER_DIALOG_ACTION,
        label: actionName,
        context: stringifyWithUndefinedKeys({
            cartId,
            ...additionalDetails,
        }),
    });
};

export const surgeChargeOrderDialogClickActionEvent = (
    actionName: string,
    cartId: string,
    businessLine: string,
    clickEvent: AnalyticsContextValue["clickEvent"],
): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT,
        action: CLICK_ACTIONS.CLICK_SURGE_CHARGE_ORDER_DIALOG_ACTION,
        label: actionName,
        context: cartId,
        extraParams: JSON.stringify({
            businessLine,
        }),
    });
};

export const surgeChangeOrderDialogImpressionEvent = (
    cartId: string,
    finalAmount: string,
    businessLine: string,
    impressionEvent: AnalyticsContextValue["impressionEvent"],
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAYMENT,
        action: IMPRESSION_ACTIONS.IMPRESSION_SURGE_ORDER_DIALOG,
        label: finalAmount,
        context: cartId,
        extraParams: JSON.stringify({
            businessLine,
        }),
    });
};

export const paymentAlreadyDoneImpressionEvent = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartAmount: number,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAY_ON_DELIVERY_PAYMENT_ALREADY_DONE_SCREEN,
        action: IMPRESSION_ACTIONS.IMPRESSION_PAY_ON_DELIVERY_PAYMENT_ALREADY_DONE,
        context: String(cartAmount),
    });
};

export const pastPaymentPendingAutoPayImpressionEvent = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartAmount: number,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAY_ON_DELIVERY_PAST_PAYMENT_PENDING_SCREEN,
        action: IMPRESSION_ACTIONS.IMPRESSION_PAY_ON_DELIVERY_PAST_PAYMENT_PENDING,
        context: String(cartAmount),
    });
};

export const pastPaymentPendingAutoPayClickEvent = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    cartAmount: number,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAY_ON_DELIVERY_PAST_PAYMENT_PENDING_SCREEN,
        action: CLICK_ACTIONS.CLICK_CONTINUE,
        context: String(cartAmount),
    });
};

export const maxPaymentRetriesAutoPayImpressionEvent = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    cartAmount: number,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAY_ON_DELIVERY_MAX_PAYMENT_RETRIES_SCREEN,
        action: IMPRESSION_ACTIONS.IMPRESSION_PAY_ON_DELIVERY_MAX_PAYMENT_RETRIES,
        context: String(cartAmount),
    });
};

export const maxPaymentRetriesAutoPayClickEvent = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    cartAmount: number,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAY_ON_DELIVERY_MAX_PAYMENT_RETRIES_SCREEN,
        action: CLICK_ACTIONS.CLICK_PAY_ON_DELIVERY_MAX_PAYMENT_RETRIES,
        context: String(cartAmount),
    });
};

export const cardRateLimitClickEvent = (clickEvent: AnalyticsContextValue["clickEvent"], label: LABELS): void => {
    clickEvent({
        category: SCREEN_NAME.PAYMENT,
        action: IMPRESSION_ACTIONS.IMPRESSION_PAYMENT_ERROR,
        label,
    });
};
// soft failure means even though you have exceeded max retries you can still keep on making payments

export const maxPaymentRetriesAutoPaySoftFailureImpressionEvent = (
    impressionEvent: AnalyticsContextValue["impressionEvent"],
    action: string,
    cartAmount: number,
): void => {
    impressionEvent({
        category: SCREEN_NAME.PAY_ON_DELIVERY_MAX_PAYMENT_RETRIES_SOFT_FAILURE_SCREEN,
        action,
        context: String(cartAmount),
    });
};

export const maxPaymentRetriesAutoPaySoftFailureClickEvent = (
    clickEvent: AnalyticsContextValue["clickEvent"],
    action: string,
    cartAmount: number,
): void => {
    clickEvent({
        category: SCREEN_NAME.PAY_ON_DELIVERY_MAX_PAYMENT_RETRIES_SOFT_FAILURE_SCREEN,
        action,
        context: String(cartAmount),
    });
};
